import { Table } from 'reactstrap';
import Category from '../marinetools/components/selftest/containers/Category.jsx';
import lateralStarboard from '../../assets/iala/lateral_starboard3.png';
import lateralPort from '../../assets/iala/lateral_port3.png';
import cardinalNorth from '../../assets/iala/cardinal_north1.png';
import cardinalEast from '../../assets/iala/cardinal_east1.png';
import cardinalSouth from '../../assets/iala/cardinal_south1.png';
import cardinalWest from '../../assets/iala/cardinal_west1.png';
import isolatedDanger from '../../assets/iala/isolateddanger1.png';
import safeWater from '../../assets/iala/clearwater1.png';
import special from '../../assets/iala/special5.png';
import emergencyWreck from '../../assets/iala/emergencywreck2.png';
import preferredStarboard from '../../assets/iala/preferred_starboard1.png';
import preferredPort from '../../assets/iala/preferred_port1.png';
import lateralDiagram from '../../assets/iala/lateral.png';
import cardinalDiagram from '../../assets/iala/image.png';

const MarkImg = ({ src, alt }) => <img src={src} alt={alt} className="as-mark-img" />;

export default function IalaBuoyageSystemRegionA() {
  return (
    <section className="as-section">
      <div className="container">
        <h1 className="as-page-title">IALA Buoyage System Region A</h1>
        <p className="fst-italic">
          If you just want to prep for Oral Exam, go directly to the{' '}
          <a href="#progress-check">Progress Check</a> to test your knowledge on the IALA
          Buoyage system.
        </p>

        <h2 className="as-subheading">Introduction to the IALA Buoyage System</h2>
        <p>
          The International Association of Marine Aids to Navigation and Lighthouse
          Authorities (IALA) Buoyage System is a globally standardized system of
          navigation marks that ensures maritime safety worldwide. Prior to 1980, more
          than 30 different buoyage systems existed around the world, leading to
          confusion and tragic maritime accidents.
        </p>
        <p>
          In 1971, the merchant ships Brandenburg and Niki sank in the English Channel
          after striking recent, well-marked wrecks, resulting in the loss of 51 lives.
          This catastrophe prompted the international maritime community to establish a
          unified buoyage system.
        </p>

        <h3 className="as-subheading-sm">The IALA Solution</h3>
        <p>IALA developed a comprehensive system divided into two regions to accommodate historical navigation practices while maintaining safety standards:</p>
        <ul>
          <li><strong>Region A:</strong> Europe, Africa, Australia, New Zealand, Middle East, and most of Asia</li>
          <li><strong>Region B:</strong> Americas, Japan, South Korea, Philippines, and Taiwan</li>
        </ul>
        <p>As Australia is in Region A, this training focuses exclusively on IALA-A characteristics.</p>

        <h3 className="as-subheading-sm">Purpose of the IALA Buoyage System</h3>
        <p>The system serves to:</p>
        <ul>
          <li>Mark navigable channels and indicate safe water</li>
          <li>Identify hazards and dangers to navigation</li>
          <li>Provide consistent, recognisable navigation aids worldwide</li>
          <li>Enable mariners to determine their position and safe passage</li>
          <li>Reduce maritime accidents through standardised communication</li>
        </ul>

        <h3 className="as-subheading-sm">The Six Types of Marks</h3>
        <p>The IALA system consists of six distinct mark types, each with specific colors, shapes, topmarks, and light characteristics:</p>
        <Table bordered responsive size="sm" className="as-data-table">
          <thead><tr><th>Mark Type</th><th>Primary Purpose</th><th>Color</th><th>Example</th></tr></thead>
          <tbody>
            <tr><td>Lateral Marks</td><td>Mark channel edges (port/starboard)</td><td>Red (port) / Green (starboard)</td><td><MarkImg src={lateralStarboard} alt="Lateral starboard mark" /></td></tr>
            <tr><td>Cardinal Marks</td><td>Indicate safe water by compass direction</td><td>Black and Yellow</td><td><MarkImg src={cardinalNorth} alt="Cardinal mark" /></td></tr>
            <tr><td>Isolated Danger Marks</td><td>Mark isolated hazards with safe water around</td><td>Black with Red bands</td><td><MarkImg src={isolatedDanger} alt="Isolated danger mark" /></td></tr>
            <tr><td>Safe Water Marks</td><td>Indicate safe water all around (mid-channel)</td><td>Red and White vertical stripes</td><td><MarkImg src={safeWater} alt="Safe water mark" /></td></tr>
            <tr><td>Special Marks</td><td>Indicate special areas or features</td><td>Yellow</td><td><MarkImg src={special} alt="Special mark" /></td></tr>
            <tr><td>Emergency Wreck Marks</td><td>Mark newly discovered wrecks/hazards</td><td>Blue and Yellow vertical stripes</td><td><MarkImg src={emergencyWreck} alt="Emergency wreck mark" /></td></tr>
          </tbody>
        </Table>
        <p className="as-callout">
          Important: Lateral marks are the ONLY marks that differ between IALA Region A
          and Region B. All other marks maintain the same characteristics worldwide.
        </p>

        <h3 className="as-subheading-sm">Understanding Mark Characteristics</h3>
        <p>Each navigation mark is identified by a combination of four key characteristics:</p>
        <ul>
          <li><strong>Color:</strong> The painted color scheme of the mark</li>
          <li><strong>Shape:</strong> Physical shape (pillar, spar, can, cone, sphere)</li>
          <li><strong>Topmark:</strong> The distinctive shape mounted on top of the mark</li>
          <li><strong>Light Characteristic:</strong> The pattern and color of light displayed at night</li>
        </ul>
        <p>Always consult your nautical chart to confirm mark identification and understand local conditions.</p>

        <h2 className="as-subheading">1. Lateral Marks</h2>
        <p>
          Lateral marks indicate the port (left) and starboard (right) sides of
          navigable channels. The term &ldquo;lateral&rdquo; means &ldquo;side,&rdquo;
          and these marks show mariners which side of the channel to navigate.
        </p>

        <h3 className="as-subheading-sm">Understanding Direction of Buoyage</h3>
        <p>The direction of buoyage is critical for correctly interpreting lateral marks. It is defined as:</p>
        <ul>
          <li>When entering harbors: From sea toward the harbor</li>
          <li>When traveling rivers: Upstream toward the river&rsquo;s source</li>
          <li>Along coasts: Generally clockwise around continents (in the direction that would take you into major harbors)</li>
        </ul>
        <p>Chart Reference: When there may be doubt about the direction of buoyage, it will be clearly marked on your nautical chart using the symbol: &#10148;</p>

        <h3 className="as-subheading-sm">IALA Region A &ndash; Lateral Mark Characteristics</h3>
        <Table bordered responsive size="sm" className="as-data-table">
          <thead><tr><th>Characteristic</th><th>Port Lateral Mark</th><th>Starboard Lateral Mark</th></tr></thead>
          <tbody>
            <tr><td>Marker</td><td><MarkImg src={lateralPort} alt="Port lateral mark" /></td><td><MarkImg src={lateralStarboard} alt="Starboard lateral mark" /></td></tr>
            <tr><td>Color</td><td>Red</td><td>Green</td></tr>
            <tr><td>Shape</td><td>Can (cylindrical) or Pillar with flat top</td><td>Cone (conical) or Pillar with pointed top</td></tr>
            <tr><td>Topmark</td><td>Single red cylinder (can shape) &ndash; if fitted</td><td>Single green cone (point up) &ndash; if fitted</td></tr>
            <tr><td>Light Color</td><td>Red (if lit)</td><td>Green (if lit)</td></tr>
            <tr><td>Light Rhythm</td><td>Any rhythm EXCEPT Fl(2+1)</td><td>Any rhythm EXCEPT Fl(2+1)</td></tr>
            <tr><td>Numbering</td><td>Even numbers (if numbered)</td><td>Odd numbers (if numbered)</td></tr>
          </tbody>
        </Table>

        <h3 className="as-subheading-sm">How to Navigate Lateral Marks</h3>
        <p><strong>When Proceeding IN THE DIRECTION of Buoyage (Entering Harbor/Going Upstream):</strong></p>
        <ul>
          <li>Keep RED PORT marks on your PORT (left) side</li>
          <li>Keep GREEN STARBOARD marks on your STARBOARD (right) side</li>
        </ul>
        <p><strong>When Proceeding AGAINST the Direction of Buoyage (Leaving Harbor/Going Downstream):</strong></p>
        <ul>
          <li>Keep RED PORT marks on your STARBOARD (right) side</li>
          <li>Keep GREEN STARBOARD marks on your PORT (left) side</li>
        </ul>

        <img src={lateralDiagram} alt="Lateral marks navigation diagram" className="as-diagram-img" />

        <h3 className="as-subheading-sm">Preferred Channel Marks (Modified Lateral Marks)</h3>
        <p>When a channel divides or splits, preferred channel marks indicate the recommended route. These are lateral marks with an additional horizontal band of the opposite color:</p>
        <Table bordered responsive size="sm" className="as-data-table">
          <thead><tr><th>Main Channel Direction</th><th>Color Scheme</th><th>Meaning</th><th>Example</th></tr></thead>
          <tbody>
            <tr><td>Straight ahead, with secondary channel to PORT</td><td>Red with GREEN horizontal band in middle</td><td>Preferred channel: Keep to starboard (treat as port mark for main channel)</td><td><MarkImg src={preferredPort} alt="Preferred channel to port mark" /></td></tr>
            <tr><td>Straight ahead, with secondary channel to STARBOARD</td><td>Green with RED horizontal band in middle</td><td>Preferred channel: Keep to port (treat as starboard mark for main channel)</td><td><MarkImg src={preferredStarboard} alt="Preferred channel to starboard mark" /></td></tr>
          </tbody>
        </Table>

        <h3 className="as-subheading-sm">Light Characteristics</h3>
        <p>Lateral marks may display various light rhythms to aid identification:</p>
        <ul>
          <li><strong>Flashing (Fl):</strong> Single flash at regular intervals &ndash; longer darkness than light</li>
          <li><strong>Quick Flashing (Q):</strong> 50-60 flashes per minute</li>
          <li><strong>Very Quick Flashing (VQ):</strong> 100-120 flashes per minute</li>
          <li><strong>Composite Group Flashing (2+1):</strong> RESERVED for preferred channel marks only</li>
        </ul>
        <p className="as-callout">
          Critical Note: Lateral marks are NOT always in pairs. When you encounter a
          single lateral mark, you must consider the direction of buoyage to determine
          which side to pass.
        </p>

        <h3 className="as-subheading-sm">Common Lateral Mark Applications</h3>
        <ul>
          <li>Marking harbor approaches and entrances</li>
          <li>Defining navigable channels in rivers and estuaries</li>
          <li>Indicating safe passage through reef systems</li>
          <li>Marking dredged channels in shallow waters</li>
        </ul>

        <h2 className="as-subheading">2. Cardinal Marks</h2>
        <p>
          Cardinal marks are named after the four cardinal points of the compass (North,
          East, South, West) and indicate the direction where safe water lies relative to
          the mark or point of interest. Unlike lateral marks, cardinal marks are
          meaningful regardless of the vessel&rsquo;s direction of travel or position.
        </p>

        <img src={cardinalDiagram} alt="Cardinal marks safe water diagram" className="as-diagram-img" />

        <h3 className="as-subheading-sm">When to Use Cardinal Marks</h3>
        <p>Cardinal marks are used to:</p>
        <ul>
          <li>Indicate that the deepest or safest water is on the named side of the mark/point of interest</li>
          <li>Draw attention to hazards such as bends, junctions, or bifurcations in channels</li>
          <li>Mark the end of shoals or shallow areas</li>
          <li>Identify new dangers (sometimes doubled up for emphasis)</li>
          <li>Mark areas where lateral marks would be confusing or inappropriate</li>
        </ul>
        <p className="as-callout">
          Navigation Rule: Always pass on the NAMED SIDE of a cardinal mark. If you see a
          North Cardinal Mark, safe water is to the NORTH &ndash; navigate north of the
          mark.
        </p>

        <h3 className="as-subheading-sm">Cardinal Mark Characteristics &ndash; Complete Table</h3>
        <Table bordered responsive size="sm" className="as-data-table">
          <thead><tr><th>Feature</th><th>North Cardinal</th><th>East Cardinal</th><th>South Cardinal</th><th>West Cardinal</th></tr></thead>
          <tbody>
            <tr><td>Marker</td><td><MarkImg src={cardinalNorth} alt="North cardinal mark" /></td><td><MarkImg src={cardinalEast} alt="East cardinal mark" /></td><td><MarkImg src={cardinalSouth} alt="South cardinal mark" /></td><td><MarkImg src={cardinalWest} alt="West cardinal mark" /></td></tr>
            <tr><td>Safe Water Direction</td><td>NORTH of mark</td><td>EAST of mark</td><td>SOUTH of mark</td><td>WEST of mark</td></tr>
            <tr><td>Color Pattern (top to bottom)</td><td>Black above Yellow</td><td>Black &ndash; Yellow &ndash; Black</td><td>Yellow above Black</td><td>Yellow &ndash; Black &ndash; Yellow</td></tr>
            <tr><td>Topmark (2 cones)</td><td>Both points UP</td><td>Points away from each other (base to base)</td><td>Both points DOWN</td><td>Points toward each other (point to point)</td></tr>
            <tr><td>Light Color</td><td>White</td><td>White</td><td>White</td><td>White</td></tr>
            <tr><td>Light Rhythm</td><td>Q or VQ (Continuous)</td><td>Q(3) or VQ(3) every 10s (or 5s)</td><td>Q(6)+LFl or VQ(6)+LFl every 15s (or 10s)</td><td>Q(9) or VQ(9) every 15s (or 10s)</td></tr>
            <tr><td>Clock Face Memory Aid</td><td>12 o&rsquo;clock &ndash; Continuous</td><td>3 o&rsquo;clock &ndash; 3 flashes</td><td>6 o&rsquo;clock &ndash; 6 flashes + long</td><td>9 o&rsquo;clock &ndash; 9 flashes</td></tr>
          </tbody>
        </Table>

        <h3 className="as-subheading-sm">Understanding the Design Logic</h3>
        <p>Color Pattern Logic: The black bands always appear where the topmark cones point.</p>
        <ul>
          <li>North: Cones point UP &rarr; Black at TOP</li>
          <li>South: Cones point DOWN &rarr; Black at BOTTOM</li>
          <li>East: Cones point OUTWARD &rarr; Black at TOP and BOTTOM (outside)</li>
          <li>West: Cones point INWARD &rarr; Black in MIDDLE</li>
        </ul>

        <h3 className="as-subheading-sm">Memory Aids for Cardinal Marks</h3>
        <p>Light Pattern &ndash; Clock Face Method: Imagine standing in the center of a clock face. The cardinal direction corresponds to the hour position:</p>
        <ul>
          <li>North (12 o&rsquo;clock): Continuous flashing</li>
          <li>East (3 o&rsquo;clock): 3 flashes</li>
          <li>South (6 o&rsquo;clock): 6 flashes + 1 long flash</li>
          <li>West (9 o&rsquo;clock): 9 flashes</li>
        </ul>

        <h3 className="as-subheading-sm">Why the South Mark Has a Long Flash</h3>
        <p>
          The South cardinal mark includes a long flash (minimum 2 seconds) after its 6
          quick flashes. This prevents confusion with the 3 or 9 flashes of East and West
          marks, especially in difficult conditions where counting quick flashes can be
          challenging.
        </p>

        <h3 className="as-subheading-sm">Quick vs. Very Quick Flashing</h3>
        <Table bordered responsive size="sm" className="as-data-table">
          <thead><tr><th>Light Type</th><th>Flashes per Minute</th><th>Use Case</th></tr></thead>
          <tbody>
            <tr><td>Quick Flashing (Q)</td><td>50-60</td><td>Standard cardinal marks</td></tr>
            <tr><td>Very Quick Flashing (VQ)</td><td>100-120</td><td>Used when two similar marks are nearby to distinguish them</td></tr>
          </tbody>
        </Table>

        <h3 className="as-subheading-sm">Shape of Cardinal Marks</h3>
        <p>Cardinal marks are typically:</p>
        <ul>
          <li><strong>Pillar buoys:</strong> Tall, vertical structures</li>
          <li><strong>Spar buoys:</strong> Pole-like markers</li>
        </ul>
        <p>The topmark (two black cones) is the most important daytime identifier and should be as large as possible with clear separation between the cones.</p>

        <h3 className="as-subheading-sm">Practical Navigation Examples</h3>
        <p><strong>Example 1 &ndash; Submerged Rock:</strong> A rock lies just below the surface. A West Cardinal mark is positioned near it. Safe water is to the WEST of the mark, meaning the hazard is to the EAST. You should pass to the west of the mark.</p>
        <p><strong>Example 2 &ndash; Channel Bend:</strong> An East Cardinal mark is placed at a sharp bend. This indicates that safe, deep water lies to the EAST of the mark &ndash; steer eastward when you see this mark.</p>
        <p><strong>Example 3 &ndash; Shoal End:</strong> A North Cardinal mark indicates the northern end of a shoal. Navigate to the NORTH of this mark to stay in safe water.</p>

        <h2 className="as-subheading">3. Isolated Danger Marks</h2>
        <p>
          Isolated danger marks are positioned on, above, or very close to isolated
          hazards of limited extent that have navigable water all around them. These
          marks warn mariners of specific dangers such as submerged rocks, wrecks, or
          small shoals that are surrounded by safe water.
        </p>
        <Table bordered responsive size="sm" className="as-data-table">
          <thead><tr><th>Characteristic</th><th>Description</th></tr></thead>
          <tbody>
            <tr><td>Marker</td><td><MarkImg src={isolatedDanger} alt="Isolated danger mark" /></td></tr>
            <tr><td>Primary Color</td><td>Black</td></tr>
            <tr><td>Band(s)</td><td>One or more broad horizontal RED bands</td></tr>
            <tr><td>Shape</td><td>Pillar or Spar (shape not critical for identification)</td></tr>
            <tr><td>Topmark</td><td>Two BLACK SPHERES, one above the other (vertically stacked)</td></tr>
            <tr><td>Light Color</td><td>White (if fitted)</td></tr>
            <tr><td>Light Rhythm</td><td>Group Flashing (2) &ndash; abbreviated as Fl(2)</td></tr>
          </tbody>
        </Table>

        <h3 className="as-subheading-sm">Critical Differences from Other Marks</h3>
        <Table bordered responsive size="sm" className="as-data-table">
          <thead><tr><th>Feature</th><th>Isolated Danger Mark</th><th>Cardinal Mark</th></tr></thead>
          <tbody>
            <tr><td>Safe Water Location</td><td>ALL AROUND the mark</td><td>Only on the NAMED SIDE</td></tr>
            <tr><td>Mark Position</td><td>ON or ABOVE the hazard</td><td>NEAR the hazard, indicating direction</td></tr>
            <tr><td>Colors</td><td>Black with Red bands</td><td>Black and Yellow bands</td></tr>
            <tr><td>Topmark</td><td>Two BLACK SPHERES (vertical)</td><td>Two BLACK CONES (various orientations)</td></tr>
            <tr><td>Light Pattern</td><td>Fl(2) &ndash; white</td><td>Q or VQ in specific patterns</td></tr>
          </tbody>
        </Table>

        <h3 className="as-subheading-sm">Chart Representation</h3>
        <p>
          On nautical charts, the position of a danger is marked at the center of the
          hazard symbol or sounding. Because an isolated danger mark is necessarily
          positioned slightly offset from the exact center (it&rsquo;s placed ON the
          danger, not floating above the chart&rsquo;s plotted center), the mark&rsquo;s
          position on the chart may appear slightly displaced from the hazard symbol.
        </p>

        <h3 className="as-subheading-sm">Practical Examples</h3>
        <p><strong>Example 1 &ndash; Offshore Rock:</strong> A submerged rock lies 2 nautical miles offshore in otherwise deep water. An isolated danger mark with two black spheres sits above it, displaying Fl(2) white light at night. Mariners can safely navigate around this mark on any side, maintaining appropriate clearance as indicated on the chart.</p>
        <p><strong>Example 2 &ndash; Wreck Site:</strong> A sunken vessel lies in a shipping lane. An isolated danger mark (black with red band, two sphere topmark) is moored directly over the wreck site. Vessels should give this mark a wide berth but can pass on any side according to their course.</p>

        <h2 className="as-subheading">4. Safe Water Marks</h2>
        <p>
          Safe water marks indicate that there is navigable water all around the mark.
          They are commonly used as mid-channel markers, center-line buoys, or landfall
          marks when approaching from seaward. These marks provide positive indication of
          safe water and guide vessels along the safest route.
        </p>
        <Table bordered responsive size="sm" className="as-data-table">
          <thead><tr><th>Characteristic</th><th>Description</th></tr></thead>
          <tbody>
            <tr><td>Marker</td><td><MarkImg src={safeWater} alt="Safe water mark" /></td></tr>
            <tr><td>Color Pattern</td><td>RED and WHITE VERTICAL STRIPES</td></tr>
            <tr><td>Shape</td><td>Spherical (preferred), Pillar, or Spar</td></tr>
            <tr><td>Topmark</td><td>Single RED SPHERE (whenever practicable on pillar/spar)</td></tr>
            <tr><td>Light Color</td><td>White (if fitted)</td></tr>
            <tr><td>Light Rhythm Options</td><td>Isophase (Iso), Occulting (Occ), Long Flash (LFl), Morse Code &ldquo;A&rdquo; (&bull; &mdash;)</td></tr>
            <tr><td>Typical Period</td><td>10 seconds for Long Flash (LFl 10s)</td></tr>
          </tbody>
        </Table>
        <p className="as-callout">
          Unique Identifier: Safe water marks are the ONLY marks in the IALA system with
          VERTICAL STRIPES. This makes them instantly recognisable and distinguishable
          from danger marks which use horizontal bands.
        </p>

        <h3 className="as-subheading-sm">Common Applications</h3>
        <Table bordered responsive size="sm" className="as-data-table">
          <thead><tr><th>Application</th><th>Purpose</th><th>Example</th></tr></thead>
          <tbody>
            <tr><td>Mid-Channel Marks</td><td>Mark the centerline of navigable channels</td><td>Brisbane River entrance channel centerline</td></tr>
            <tr><td>Landfall Buoys</td><td>First mark encountered when approaching from seaward</td><td>Harbor approach buoy 5 nautical miles offshore</td></tr>
            <tr><td>Fairway Marks</td><td>Indicate the fairway or main approach route</td><td>Port entry fairway marker</td></tr>
            <tr><td>Channel Forks</td><td>Mark safe water at channel bifurcations</td><td>Junction point where channels divide</td></tr>
          </tbody>
        </Table>

        <h3 className="as-subheading-sm">Understanding Light Characteristics</h3>
        <p>Safe water marks use distinctive white light patterns that differ from other marks:</p>
        <ul>
          <li><strong>Isophase (Iso):</strong> Light and dark periods are exactly equal in duration</li>
          <li><strong>Occulting (Occ):</strong> Light is ON longer than it is OFF (opposite of flashing)</li>
          <li><strong>Long Flash (LFl):</strong> A single flash lasting 2 seconds or more</li>
          <li><strong>Morse &ldquo;A&rdquo; (&bull; &mdash;):</strong> Short flash followed by long flash (dit-dah)</li>
        </ul>
        <p className="as-callout">
          Critical Navigation Difference: Safe water marks &ndash; you CAN and SHOULD
          navigate close to these marks, they mark safe channels. Isolated danger marks
          &ndash; you MUST keep clear, they mark hazards.
        </p>

        <h3 className="as-subheading-sm">Practical Navigation Examples</h3>
        <p><strong>Example 1 &ndash; Landfall Navigation:</strong> A vessel approaching Sydney Harbour from the open ocean encounters a large spherical buoy with red and white vertical stripes showing an isophase white light. This safe water mark indicates the beginning of the safe approach channel. The vessel should head toward this mark and use it as a reference point for entering the channel.</p>
        <p><strong>Example 2 &ndash; Mid-Channel Guidance:</strong> A series of safe water marks (red/white vertical stripes) runs down the center of a dredged channel. Vessels should follow these marks to stay in the deepest, safest water, especially important for deep-draft vessels.</p>
        <p><strong>Example 3 &ndash; Channel Junction:</strong> At a point where two channels meet, a safe water mark indicates that navigable water is available in all directions from this point. Vessels can choose their route based on their destination, knowing the water is safe around this mark.</p>

        <h3 className="as-subheading-sm">Chart Symbols and Abbreviations</h3>
        <p>On nautical charts, safe water marks are typically indicated with:</p>
        <ul>
          <li>Red and white striped symbol</li>
          <li>Sphere or buoy shape symbol</li>
          <li>Light characteristic notation (e.g., &ldquo;Iso W 10s&rdquo; or &ldquo;LFl W 10s&rdquo;)</li>
          <li>Name or number if applicable</li>
        </ul>

        <h2 className="as-subheading">5. Special Marks</h2>
        <p>
          Special marks are not primarily aids to navigation in the traditional sense.
          Instead, they indicate special areas or features whose nature can be determined
          by consulting nautical charts or other maritime publications. These marks
          provide information rather than warn of dangers or mark channels.
        </p>
        <Table bordered responsive size="sm" className="as-data-table">
          <thead><tr><th>Characteristic</th><th>Description</th></tr></thead>
          <tbody>
            <tr><td>Marker</td><td><MarkImg src={special} alt="Special mark" /></td></tr>
            <tr><td>Color</td><td>YELLOW (entire mark)</td></tr>
            <tr><td>Shape</td><td>Optional &ndash; can be any shape (Can, Cone, Sphere, Pillar, Spar) but must not conflict with Lateral or Safe Water mark shapes in same location</td></tr>
            <tr><td>Topmark</td><td>Yellow &ldquo;X&rdquo; (St. Andrew&rsquo;s Cross) &ndash; optional but usually fitted</td></tr>
            <tr><td>Light Color</td><td>Yellow (if fitted)</td></tr>
            <tr><td>Light Rhythm</td><td>Any rhythm NOT used by Cardinal, Isolated Danger, or Safe Water marks. Commonly: Fl(4) Y</td></tr>
          </tbody>
        </Table>
        <p>Identification: If you see a completely YELLOW mark, it&rsquo;s a special mark. The yellow color immediately distinguishes it from all other mark types in the IALA system.</p>

        <h3 className="as-subheading-sm">Common Uses for Special Marks</h3>
        <Table bordered responsive size="sm" className="as-data-table">
          <thead><tr><th>Category</th><th>Specific Applications</th></tr></thead>
          <tbody>
            <tr><td>Marine Infrastructure</td><td>Ocean Data Acquisition Systems (ODAS); cables and pipelines; outfall pipes and discharge points; oil wells and platforms</td></tr>
            <tr><td>Boundaries and Zones</td><td>Traffic separation scheme boundaries; anchorage area limits; marine protected areas; speed restriction zones; exclusion zones</td></tr>
            <tr><td>Aquaculture</td><td>Marine farms (fish, oyster, mussel); aquaculture lease boundaries; fish aggregating devices</td></tr>
            <tr><td>Military and Recreation</td><td>Military exercise areas; naval gunnery ranges; racing marks; water skiing areas; swimming zones</td></tr>
            <tr><td>Navigation Features</td><td>Spoil grounds (dredge dumping); mooring areas; dead-end channels; channel junctions (supplementary)</td></tr>
          </tbody>
        </Table>

        <h3 className="as-subheading-sm">Light Characteristics</h3>
        <p>Special mark lights are designed to be distinctive:</p>
        <ul>
          <li><strong>Color:</strong> Always YELLOW when lit</li>
          <li><strong>Rhythm:</strong> Must differ from other IALA marks to avoid confusion</li>
          <li>Common patterns: Fl(4) Y (very common), Fl(5) Y, Fl Y, or any distinctive yellow rhythm not used elsewhere</li>
        </ul>
        <p>Why Fl(4) is Common: Group flashing (4) is popular for special marks because no other mark with a WHITE light uses this rhythm, preventing confusion with cardinal, isolated danger, and safe water marks.</p>

        <h3 className="as-subheading-sm">Navigating Near Special Marks</h3>
        <ul>
          <li>Identify the yellow special mark</li>
          <li>Consult your nautical chart for the specific area or feature</li>
          <li>Follow any regulations or restrictions for that area</li>
          <li>Special marks do NOT indicate safe or dangerous water &ndash; they only mark special areas</li>
        </ul>

        <h3 className="as-subheading-sm">Practical Examples</h3>
        <p><strong>Example 1 &ndash; Cable Crossing:</strong> Yellow special marks with X topmarks are positioned on either side of an underwater cable crossing. Charts indicate &ldquo;Cable Area &ndash; No Anchoring.&rdquo; Mariners should not anchor between or near these marks to avoid damaging the cable.</p>
        <p><strong>Example 2 &ndash; Aquaculture Zone:</strong> A series of yellow can-shaped buoys with X topmarks outline a marine farm area. Charts indicate &ldquo;Oyster Lease &ndash; Keep Clear.&rdquo; Vessels should navigate around this marked area and not enter the aquaculture zone.</p>
        <p><strong>Example 3 &ndash; Military Exercise Area:</strong> Yellow pillar buoys mark the boundaries of a naval gunnery range. Charts indicate &ldquo;Military Exercise Area &ndash; Entry Prohibited When Active.&rdquo; Mariners should check NOTAMs (Notices to Mariners) before entering.</p>
        <p><strong>Example 4 &ndash; Spoil Ground:</strong> Yellow special marks indicate a dredge spoil dumping area. Charts show &ldquo;Spoil Ground &ndash; Depths Variable.&rdquo; Vessels should exercise caution as depths may be shallower than surrounding areas and subject to change.</p>

        <h3 className="as-subheading-sm">Difference from Emergency Wreck Marks</h3>
        <Table bordered responsive size="sm" className="as-data-table">
          <thead><tr><th>Feature</th><th>Special Mark</th><th>Emergency Wreck Mark</th></tr></thead>
          <tbody>
            <tr><td>Color</td><td>Solid Yellow</td><td>Blue and Yellow vertical stripes</td></tr>
            <tr><td>Topmark</td><td>Yellow X</td><td>Yellow Cross (upright +)</td></tr>
            <tr><td>Purpose</td><td>Mark special areas/features</td><td>Mark new uncharted dangers/wrecks</td></tr>
            <tr><td>Urgency</td><td>Informational</td><td>High urgency warning</td></tr>
            <tr><td>Duration</td><td>Permanent or long-term</td><td>Temporary until charted</td></tr>
          </tbody>
        </Table>
        <p className="as-callout">
          Important Note: Special marks do not indicate whether water is safe or
          dangerous. They only mark areas with special features. Always consult charts
          and publications to understand what the special mark indicates and what
          actions you should take.
        </p>

        <h3 className="as-subheading-sm">Regional Variations</h3>
        <p>While the IALA system is standardized, the specific use of special marks can vary by region and country. Common variations include:</p>
        <ul>
          <li>Different countries may use special marks for different purposes</li>
          <li>Local regulations determine what requires a special mark</li>
          <li>Some areas have many special marks, others have few</li>
          <li>Always refer to local charts and sailing directions</li>
        </ul>

        <h2 className="as-subheading">Quick Reference Summary</h2>
        <h3 className="as-subheading-sm">All Mark Types at a Glance</h3>
        <Table bordered responsive size="sm" className="as-data-table">
          <thead><tr><th>Mark Type</th><th>Colors</th><th>Topmark</th><th>Light</th><th>Meaning</th><th>Marker</th></tr></thead>
          <tbody>
            <tr><td>Lateral &ndash; Port</td><td>Red</td><td>Red Can</td><td>Red (any)</td><td>Keep on port when entering</td><td><MarkImg src={lateralPort} alt="Port lateral mark" /></td></tr>
            <tr><td>Lateral &ndash; Starboard</td><td>Green</td><td>Green Cone</td><td>Green (any)</td><td>Keep on starboard when entering</td><td><MarkImg src={lateralStarboard} alt="Starboard lateral mark" /></td></tr>
            <tr><td>Cardinal &ndash; North</td><td>Black/Yellow</td><td>2 Cones Up</td><td>White Q/VQ</td><td>Pass NORTH of mark</td><td><MarkImg src={cardinalNorth} alt="North cardinal mark" /></td></tr>
            <tr><td>Cardinal &ndash; East</td><td>Black/Yellow/Black</td><td>2 Cones Apart</td><td>White Q(3)/VQ(3)</td><td>Pass EAST of mark</td><td><MarkImg src={cardinalEast} alt="East cardinal mark" /></td></tr>
            <tr><td>Cardinal &ndash; South</td><td>Yellow/Black</td><td>2 Cones Down</td><td>White Q(6)+LFl</td><td>Pass SOUTH of mark</td><td><MarkImg src={cardinalSouth} alt="South cardinal mark" /></td></tr>
            <tr><td>Cardinal &ndash; West</td><td>Yellow/Black/Yellow</td><td>2 Cones Together</td><td>White Q(9)/VQ(9)</td><td>Pass WEST of mark</td><td><MarkImg src={cardinalWest} alt="West cardinal mark" /></td></tr>
            <tr><td>Isolated Danger</td><td>Black with Red bands</td><td>2 Black Spheres</td><td>White Fl(2)</td><td>Danger below &ndash; keep clear</td><td><MarkImg src={isolatedDanger} alt="Isolated danger mark" /></td></tr>
            <tr><td>Safe Water</td><td>Red/White vertical</td><td>Red Sphere</td><td>White Iso/Occ/LFl</td><td>Safe water all around</td><td><MarkImg src={safeWater} alt="Safe water mark" /></td></tr>
            <tr><td>Special</td><td>Yellow</td><td>Yellow X</td><td>Yellow (any)</td><td>See chart for details</td><td><MarkImg src={special} alt="Special mark" /></td></tr>
          </tbody>
        </Table>

        <h2 className="as-subheading" id="progress-check">Progress Check</h2>
        <p>
          Test your IALA Buoyage knowledge with the flip cards below randomly prompting
          questions and visual images. Study the image and decide on the action you would
          take and click the image to see if you got the right answer. Click the buttons
          under answer to keep score and move to next question.
        </p>

        <Category selftest_category_id="iala" />

        <p className="mt-4">
          You can use your mobile to test your knowledge on this topic as well as many
          other marine related topics required for your Marine Master Oral interview.
        </p>
      </div>
    </section>
  );
}
